import { ArrowDownIcon } from "@src/assets/general-icons";
import { MagnifyingGlassIcon } from "@src/assets/general-icons/MagnifyingGlassIcon";
import helperStyles from "@src/components/helpers.module.scss";
import { pricingActions, systemActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { ISavedRules } from "@src/store/pricing/savedRulesSlice";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button, ButtonVariant } from "../button/Button";
import styles from "./Dropdown.module.scss";
import ruleStyles from "./RulesDropdown.module.scss";
import { EyeHide } from "@src/assets/general-icons/EyeHide";
import { EyeShow } from "@src/assets/general-icons/EyeShow";
import { Trash2 } from "lucide-react";
import { getSplitsName } from "../utils/marketplaceUtils";
import DisplayMarketplace from "../displayMarketplace/DisplayMarketplace";
import { ExportDestination } from "@src/constants/constants";

interface IRulesDropdown extends React.ComponentProps<"div"> {
	data: Array<ISavedRules>;
	error: string | null;
	onRulePick: (data: ISavedRules) => void;
}

export const RulesDropdown: React.FC<IRulesDropdown> = ({ data, error, onRulePick, ...rest }) => {
	const [open, setOpen] = useState(false);
	const [picked, setPicked] = useState<ISavedRules | null>(null);
	const buttonRef = useRef<HTMLDivElement>(null);
	const [search, setSearch] = useState("");
	const dispatch = useAppDispatch();

	const { data: marketplaces } = useAppSelector((state) => state.system.exportDestinations);
	const hasCIMSMarketAccess = useAppSelector((state) => state.settings.hasCIMSMarketAccess);

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);

	const filteredRules = data.filter((i) => i.RuleName.toLowerCase().includes(search.toLowerCase()));
	const toggleOpen = () => {
		setOpen((prev) => !prev);
	};

	const mappedItems = filteredRules?.map((item) => {
		const { RuleName, RuleId, AllowedSplits, Visibility, Markups } = item;
		const isPicked = picked?.RuleName === RuleName;
		const handleSetPick = () => {
			setPicked(item);
			toggleOpen();
			onRulePick(item);
		};

		const onRuleDelete = (e: React.MouseEvent<HTMLDivElement>) => {
			e.stopPropagation();
			if (!RuleId) {
				toast.warning("Rule doesn't have an ID");
				return;
			}
			dispatch(pricingActions.deleteRule({ RuleId }));
		};

		const marketplaceIds = Markups.map((x) => x.Export_Destination_ID);
		const mappedMarketplaces = marketplaces
			.filter((x) => marketplaceIds.includes(x.Export_Destination_ID))
			.filter((x) => hasCIMSMarketAccess || x.Export_Destination_ID !== ExportDestination.Cims)
			.map((x) => <DisplayMarketplace key={x.Export_Destination_ID} data={x} />);

		return (
			<div
				data-is-selected={isPicked}
				className={ruleStyles.singleItem}
				aria-hidden="true"
				onClick={handleSetPick}
				key={RuleId}>
				<div className={ruleStyles.name}>{RuleName}</div>
				<div className={ruleStyles.seats}>{Visibility === "HIDE" ? <EyeHide /> : <EyeShow />}</div>
				<div className={ruleStyles.splits}>{getSplitsName(AllowedSplits)}</div>
				<div className={ruleStyles.sharedTo}>{mappedMarketplaces}</div>
				{RuleId ? (
					<div aria-hidden onClick={onRuleDelete} className={ruleStyles.trashCan}>
						<Trash2 size={16} />
					</div>
				) : null}
			</div>
		);
	});

	const content = mappedItems.length > 0 ? mappedItems : <div className={ruleStyles.item}>No options</div>;
	const dropdownLabel = error ?? picked?.RuleName ?? "Select saved rule";

	const handleOutsideClick = useCallback((event: MouseEvent) => {
		if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
			setOpen(false);
		}
	}, []);

	useEffect(() => {
		dispatch(systemActions.listExportDestinations({}));
	}, [dispatch]);

	useEffect(() => {
		if (open) {
			document.addEventListener("mousedown", handleOutsideClick);
		}
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, [open, handleOutsideClick]);

	return (
		<div ref={buttonRef} {...rest} className={styles.dropdownContainer}>
			<Button
				style={{ width: "100%", display: "flex", justifyContent: "start" }}
				onClick={toggleOpen}
				variant={ButtonVariant.Secondary}>
				<div className={`${styles.dropdownButton} ${helperStyles.center}`}>
					<div>{dropdownLabel}</div>
					<div>
						<ArrowDownIcon />
					</div>
				</div>
			</Button>
			{open && (
				<div className={ruleStyles.rulesItemsContainer}>
					<div className={ruleStyles.inputContainer}>
						<input placeholder="Search rule" value={search} onChange={handleSearch} />
						<MagnifyingGlassIcon />
					</div>
					<div className={ruleStyles.headerLabels}>
						<div className={ruleStyles.name}>Rule Name</div>
						<div className={ruleStyles.seats}>Seats</div>
						<div className={ruleStyles.splits}>Splits</div>
						<div className={ruleStyles.sharedTo}>Shared to</div>
					</div>
					<div className={ruleStyles.itemsContainer}>{content}</div>
				</div>
			)}
		</div>
	);
};
